import React from 'react';
import { Shield, GraduationCap, Users, ArrowRight, Star, CheckCircle } from 'lucide-react';

const Services: React.FC = () => {
  const services = [
    {
      icon: Shield,
      title: 'Sentry',
      subtitle: 'Visitors Access Management',
      description: `Sentry is a visitor access and security management application used by estate and enterprises to manage visitor access to their physical environment. 
      With Sentry, individuals can give pre-scheduled access to their visitors via their mobile phone and authorize Security to allow them to their physical environment via exchange of unique access codes that carries the Visitor's details and where (residence, office etc) they are visiting.`,
      
      features: [        
      ],
      color: 'green'
    },
    {
      icon: GraduationCap,
      title: 'School Tripp',
      subtitle: 'School Bus Tracking and Management',
      description: 'SchoolTripp provides visibility on school bus commutes for Parents and Schools. It allows School Owners/Administrators to manage their school buses on the go with enhanced monitoring as well as provide real time updates to parents on their children\'s school commute with real time alerts on their location and estimated time to arrive (ETA).',
      features: [        
      ],
      color: 'yellow'
    },
    {
      icon: Users,
      title: 'Social Riding',
      subtitle: 'Car Pooling and Ride Sharing',
      description: 'Tripp improves social connections by connecting private car drivers with vacant seats to commuters without cars or other drivers going in the same/similar destination. This connection via sharing of rides provides additional income for private car owners, reduces the number of cars on the roads which leads to reduction in traffic congestion and promotes a cleaner environment by reducing car emissions which causes air pollution.',
      features: [      
      ],
      color: 'red'
    }
  ];

  const getColorClasses = (color: string) => {
    switch (color) {
      case 'green':
        return {
          bg: 'bg-green-50',
          icon: 'text-green-700',
          button: 'bg-green-700 hover:bg-green-700',
          accent: 'text-green-700'
        };
      case 'purple':
        return {
          bg: 'bg-purple-50',
          icon: 'text-purple-600',
          button: 'bg-purple-600 hover:bg-purple-700',
          accent: 'text-purple-600'
        };
      case 'orange':
        return {
          bg: 'bg-orange-50',
          icon: 'text-orange-600',
          button: 'bg-orange-600 hover:bg-orange-700',
          accent: 'text-orange-600'
        };
      default:
        return {
          bg: 'bg-green-50',
          icon: 'text-green-700',
          button: 'bg-green-700 hover:bg-green-800',
          accent: 'text-green-700'
        };
    }
  };

  return (
    <div className="py-24 bg-green-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Services</h2>
          <div className="w-24 h-1 bg-green-700 mx-auto mb-6"></div>        
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16">
          {services.map((service, index) => {
            const colors = getColorClasses(service.color);
            return (
              <div key={index} className="bg-white rounded-2xl shadow-xl hover:shadow-2xl transition-shadow duration-300 overflow-hidden">
                <div className={`${colors.bg} p-8`}>
                  <div className="flex items-center justify-between mb-6">
                    <div className={`w-16 h-16 ${colors.bg} rounded-full flex items-center justify-center`}>
                      <service.icon className={`h-8 w-8 ${colors.icon}`} />
                    </div>
                    <div className="flex items-center space-x-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{service.title}</h3>
                  <p className={`text-sm font-medium ${colors.accent} mb-4`}>{service.subtitle}</p>
                  <p className="text-gray-600 leading-relaxed mb-6">{service.description}</p>
                </div>
                
                <div className="p-8">
                  <div className="space-y-3 mb-8">
                    {service.features.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-center space-x-3">
                        <CheckCircle className="h-5 w-5 text-green-700 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                  
                  
                </div>
              </div>
            );
          })}
        </div>

        <div className="bg-gradient-to-r from-green-700 to-green-800 rounded-2xl p-12 text-center text-white">
          <h3 className="text-3xl font-bold mb-4">Ready to Get Started?</h3>
          <p className="text-xl mb-8 opacity-90">
            Choose the perfect service by downloading the app.
          </p>
            <div className="flex flex-col sm:flex-row gap-4 items-center justify-center" >
              {/* Google Play Store Button */}
              <a 
                href="https://play.google.com/store/apps/details?id=ng.tripp" 
                className="bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center space-x-3 min-w-[200px]"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">GET IT ON</div>
                  <div className="text-sm font-semibold">Google Play</div>
                </div>
              </a>
              
              {/* Apple App Store Button */}
              <a 
                href="https://apps.apple.com/ng/app/tripp-platform-services/id1670047509" 
                className="bg-black text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors duration-200 flex items-center justify-center space-x-3 min-w-[200px]"
              >
                <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.71,19.5C17.88,20.74 17,21.95 15.66,21.97C14.32,22 13.89,21.18 12.37,21.18C10.84,21.18 10.37,21.95 9.1,22C7.79,22.05 6.8,20.68 5.96,19.47C4.25,17 2.94,12.45 4.7,9.39C5.57,7.87 7.13,6.91 8.82,6.88C10.1,6.86 11.32,7.75 12.11,7.75C12.89,7.75 14.37,6.68 15.92,6.84C16.57,6.87 18.39,7.1 19.56,8.82C19.47,8.88 17.39,10.1 17.41,12.63C17.44,15.65 20.06,16.66 20.09,16.67C20.06,16.74 19.67,18.11 18.71,19.5M13,3.5C13.73,2.67 14.94,2.04 15.94,2C16.07,3.17 15.6,4.35 14.9,5.19C14.21,6.04 13.07,6.7 11.95,6.61C11.8,5.46 12.36,4.26 13,3.5Z"/>
                </svg>
                <div className="text-left">
                  <div className="text-xs">Download on the</div>
                  <div className="text-sm font-semibold">App Store</div>
                </div>
              </a>
            </div>
          
        </div>
      </div>
    </div>
  );
};

export default Services;